import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAssets, getAsset, createAsset, updateAsset, assignAsset, returnAsset, disposeAsset, deleteAsset, getMembers } from '../../services/api';
import { FiPlus, FiTrash2, FiX, FiPackage, FiClock, FiArrowRight, FiCornerUpLeft } from 'react-icons/fi';
import Toast from '../../components/Toast';
import { useAuth } from '../../context/AuthContext';

const statusPill = { in_store: 'pill-green', in_use: 'pill-blue', disposed: 'pill-gray' };
const statusLabel = { in_store: 'In Store', in_use: 'In Use', disposed: 'Disposed' };
const condPill = { good: 'pill-green', fair: 'pill-yellow', needs_repair: 'pill-red', damaged: 'pill-red' };
const condLabel = { good: 'Good', fair: 'Fair', needs_repair: 'Needs Repair', damaged: 'Damaged' };
const CONDITIONS = ['good', 'fair', 'needs_repair', 'damaged'];

const AssetRegister = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canManage = ['admin', 'secretary', 'finance'].includes(user?.role);

  const [list, setList] = useState([]);
  const [members, setMembers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', category: '', acquired_via: 'existing', donor_member_id: '', donor_name: '', acquired_date: new Date().toISOString().slice(0, 10), value: '', condition: 'good', location: '', notes: '' });
  const [assignModal, setAssignModal] = useState(null);
  const [assignForm, setAssignForm] = useState({ location: '', notes: '' });
  const [historyModal, setHistoryModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', category: '', condition: 'good', value: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const fetchList = () => getAssets({ status: statusFilter, search }).then(r => setList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { fetchList(); }, [statusFilter, search]);
  useEffect(() => { getMembers({}).then(r => setMembers(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);

  const donorName = (a) => {
    if (a.donor_first || a.donor_last) return [a.donor_first, a.donor_middle, a.donor_last].filter(Boolean).join(' ');
    return a.donor_name || '—';
  };

  const handleAdd = async () => {
    setError('');
    if (!addForm.name.trim()) return setError('Name is required');
    if (addForm.acquired_via === 'donation' && !addForm.donor_member_id && !addForm.donor_name) return setError('Pick the donor member or type a donor name');
    setSaving(true);
    try {
      await createAsset({ ...addForm, donor_member_id: addForm.acquired_via === 'donation' ? (addForm.donor_member_id || null) : null });
      setShowAdd(false);
      setAddForm({ name: '', category: '', acquired_via: 'existing', donor_member_id: '', donor_name: '', acquired_date: new Date().toISOString().slice(0, 10), value: '', condition: 'good', location: '', notes: '' });
      showToast('Asset added');
      fetchList();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const openAssign = (a) => { setAssignModal(a); setAssignForm({ location: '', notes: '' }); };
  const handleAssign = async () => {
    if (!assignForm.location.trim()) return showToast('Enter location / holder', 'error');
    setSaving(true);
    try { await assignAsset(assignModal.id, assignForm); showToast('Asset assigned'); setAssignModal(null); fetchList(); }
    catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleReturn = async (a) => {
    if (!window.confirm(`Return "${a.name}" to the store?`)) return;
    try { await returnAsset(a.id, {}); showToast('Returned to store'); fetchList(); }
    catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
  };

  const handleDispose = async (a) => {
    const notes = window.prompt(`Dispose "${a.name}"? Optionally enter a reason:`);
    if (notes === null) return;
    try { await disposeAsset(a.id, { notes }); showToast('Asset disposed', 'error'); fetchList(); }
    catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Remove "${a.name}" from the register? This cannot be undone.`)) return;
    try { await deleteAsset(a.id); showToast('Removed', 'error'); fetchList(); }
    catch (e) { showToast('Failed', 'error'); }
  };

  const openEdit = (a) => { setEditModal(a); setEditForm({ name: a.name, category: a.category || '', condition: a.condition || 'good', value: a.value || '', notes: a.notes || '' }); };
  const handleEdit = async () => {
    setSaving(true);
    try { await updateAsset(editModal.id, editForm); showToast('Asset updated'); setEditModal(null); fetchList(); }
    catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const openHistory = (a) => {
    setHistoryModal({ ...a, history: [] });
    getAsset(a.id).then(r => setHistoryModal(r.data)).catch(() => {});
  };

  const counts = {
    in_store: list.filter(a => a.status === 'in_store').length,
    in_use: list.filter(a => a.status === 'in_use').length,
    disposed: list.filter(a => a.status === 'disposed').length,
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>Fixed Assets</h1><p>{list.length} on register</p></div>
        {canManage && <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FiPlus /> Add Asset</button>}
      </div>

      <div className="stat-cards" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 16 }}>
        <div className="stat-card"><div className="stat-icon" style={{ background: '#dcfce7', color: '#16a34a' }}><FiPackage /></div><div><div className="stat-label">In Store</div><div className="stat-value">{counts.in_store}</div></div></div>
        <div className="stat-card"><div className="stat-icon" style={{ background: '#dbeafe', color: '#2563eb' }}><FiPackage /></div><div><div className="stat-label">In Use</div><div className="stat-value">{counts.in_use}</div></div></div>
        <div className="stat-card"><div className="stat-icon" style={{ background: '#f3f4f6', color: '#6b7280' }}><FiPackage /></div><div><div className="stat-label">Disposed</div><div className="stat-value">{counts.disposed}</div></div></div>
      </div>

      <div className="filters-row">
        <input className="filter-input" placeholder="Search assets…" value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 200 }} />
        <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="in_store">In Store</option>
          <option value="in_use">In Use</option>
          <option value="disposed">Disposed</option>
        </select>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr><th>#</th><th>Name</th><th>Category</th><th>Status</th><th>Condition</th><th>Location / Holder</th><th>Source</th><th>Value</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={9}><div className="empty-state"><div className="empty-icon"><FiPackage /></div><p>No assets registered</p></div></td></tr>
            ) : list.map((a, i) => (
              <tr key={a.id}>
                <td data-label="#">{i + 1}</td>
                <td data-label="Name"><strong>{a.name}</strong></td>
                <td data-label="Category">{a.category || '—'}</td>
                <td data-label="Status"><span className={`pill ${statusPill[a.status] || 'pill-gray'}`}>{statusLabel[a.status] || a.status}</span></td>
                <td data-label="Condition"><span className={`pill ${condPill[a.condition] || 'pill-gray'}`}>{condLabel[a.condition] || a.condition}</span></td>
                <td data-label="Location / Holder">{a.status === 'in_use' ? (a.location || '—') : '—'}</td>
                <td data-label="Source" style={{ fontSize: 12 }}>
                  {a.acquired_via === 'donation' ? <span>🎁 {donorName(a)}</span> : a.acquired_via === 'purchase' ? <span>Purchase</span> : <span>Existing</span>}
                </td>
                <td data-label="Value">{parseFloat(a.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td data-label="Actions">
                  <div className="action-btns">
                    {canManage && a.status === 'in_store' && <button className="btn btn-sm btn-primary" onClick={() => openAssign(a)} title="Assign"><FiArrowRight /> Assign</button>}
                    {canManage && a.status === 'in_use' && <button className="btn btn-sm btn-secondary" onClick={() => handleReturn(a)} title="Return"><FiCornerUpLeft /> Return</button>}
                    <button className="btn-icon" onClick={() => openHistory(a)} title="History"><FiClock /></button>
                    {canManage && a.status !== 'disposed' && <button className="btn btn-sm btn-secondary" onClick={() => openEdit(a)}>Edit</button>}
                    {canManage && a.status !== 'disposed' && <button className="btn btn-sm btn-secondary" onClick={() => handleDispose(a)}>Dispose</button>}
                    {canManage && <button className="btn-icon danger" onClick={() => handleDelete(a)}><FiTrash2 /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Asset Modal */}
      {showAdd && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header"><div><h3>Add Fixed Asset</h3></div><button className="btn-icon" onClick={() => setShowAdd(false)}><FiX /></button></div>
            <div className="modal-body">
              {error && <div className="error-msg">{error}</div>}
              <div className="form-grid">
                <div className="form-group form-full"><label>Name <span className="required">*</span></label><input value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Kebero" /></div>
                <div className="form-group"><label>Category</label><input value={addForm.category} onChange={e => setAddForm(p => ({ ...p, category: e.target.value }))} placeholder="e.g. Instrument" /></div>
                <div className="form-group"><label>Condition</label>
                  <select value={addForm.condition} onChange={e => setAddForm(p => ({ ...p, condition: e.target.value }))}>
                    {CONDITIONS.map(c => <option key={c} value={c}>{condLabel[c]}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Acquired via</label>
                  <select value={addForm.acquired_via} onChange={e => setAddForm(p => ({ ...p, acquired_via: e.target.value }))}>
                    <option value="existing">Existing (already owned)</option>
                    <option value="donation">Donation (member gift)</option>
                    <option value="purchase">Purchase</option>
                  </select>
                </div>
                <div className="form-group"><label>Acquired date</label><input type="date" value={addForm.acquired_date} onChange={e => setAddForm(p => ({ ...p, acquired_date: e.target.value }))} /></div>
                {addForm.acquired_via === 'donation' && (
                  <>
                    <div className="form-group"><label>Donor (member)</label>
                      <select value={addForm.donor_member_id} onChange={e => setAddForm(p => ({ ...p, donor_member_id: e.target.value }))}>
                        <option value="">— Select member —</option>
                        {members.map(m => <option key={m.id} value={m.id}>{[m.first_name, m.middle_name, m.last_name].filter(Boolean).join(' ')}</option>)}
                      </select>
                    </div>
                    <div className="form-group"><label>or Donor name</label><input value={addForm.donor_name} onChange={e => setAddForm(p => ({ ...p, donor_name: e.target.value }))} placeholder="Non-member donor" /></div>
                  </>
                )}
                <div className="form-group"><label>Value</label><input type="number" step="0.01" value={addForm.value} onChange={e => setAddForm(p => ({ ...p, value: e.target.value }))} /></div>
                <div className="form-group form-full"><label>Notes</label><textarea rows={2} value={addForm.notes} onChange={e => setAddForm(p => ({ ...p, notes: e.target.value }))} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving ? 'Saving…' : 'Add Asset'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {assignModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header"><div><h3>Assign Asset</h3><p>{assignModal.name}</p></div><button className="btn-icon" onClick={() => setAssignModal(null)}><FiX /></button></div>
            <div className="modal-body">
              <div className="form-group"><label>Location / Holder <span className="required">*</span></label><input value={assignForm.location} onChange={e => setAssignForm(p => ({ ...p, location: e.target.value }))} placeholder="e.g. Main hall, or person's name" autoFocus /></div>
              <div className="form-group"><label>Notes</label><textarea rows={2} value={assignForm.notes} onChange={e => setAssignForm(p => ({ ...p, notes: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setAssignModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAssign} disabled={saving}>{saving ? 'Saving…' : 'Assign'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header"><div><h3>Edit Asset</h3></div><button className="btn-icon" onClick={() => setEditModal(null)}><FiX /></button></div>
            <div className="modal-body">
              <div className="form-group"><label>Name</label><input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} /></div>
              <div className="form-group"><label>Category</label><input value={editForm.category} onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))} /></div>
              <div className="form-group"><label>Condition</label>
                <select value={editForm.condition} onChange={e => setEditForm(p => ({ ...p, condition: e.target.value }))}>
                  {CONDITIONS.map(c => <option key={c} value={c}>{condLabel[c]}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Value</label><input type="number" step="0.01" value={editForm.value} onChange={e => setEditForm(p => ({ ...p, value: e.target.value }))} /></div>
              <div className="form-group"><label>Notes</label><textarea rows={2} value={editForm.notes} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEdit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header"><div><h3>Asset History</h3><p>{historyModal.name}</p></div><button className="btn-icon" onClick={() => setHistoryModal(null)}><FiX /></button></div>
            <div className="modal-body">
              {(historyModal.history || []).length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: 13 }}>No movements recorded</div>
              ) : historyModal.history.map(h => (
                <div key={h.id} style={{ fontSize: 13, color: '#374151', marginBottom: 10, paddingLeft: 10, borderLeft: '2px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, textTransform: 'capitalize' }}>{(h.action || '').replace('_', ' ')}</span>
                    {h.location && <span className="pill pill-blue" style={{ fontSize: 10 }}>{h.location}</span>}
                    {h.condition && <span className={`pill ${condPill[h.condition] || 'pill-gray'}`} style={{ fontSize: 10 }}>{condLabel[h.condition] || h.condition}</span>}
                    <span style={{ color: '#9ca3af', marginLeft: 'auto', fontSize: 11 }}>{h.created_at?.slice(0, 16)}</span>
                  </div>
                  {h.notes && <div style={{ marginTop: 2, fontStyle: 'italic', color: '#6b7280' }}>{h.notes}</div>}
                  {h.done_by_name && <div style={{ marginTop: 2, color: '#9ca3af', fontSize: 11 }}>by {h.done_by_name}</div>}
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setHistoryModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default AssetRegister;
